import { ReactNode, useEffect, useRef, useState } from 'react';
import { CalendarDays, Clock } from 'lucide-react';
import { useModal, useStep } from '@/hooks';
import { decodeUrl, encodeUrl, formatTimeSlot } from '@/utils';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
	useCreateSession,
	useGetAvailableDate,
	useGetPatient,
	useGetProfessional,
	useGetSession,
	usePatchSessionDate,
} from '@/services';

import { Avatar, AvailableDatePicker } from '@/components/ui';
import {
	ConfirmationModalContent,
	ServiceModalityFeedback,
	SessionStepTitle,
	StepButtonContainer,
	TimeSlotSelect,
} from '../components';
import { TimeSlotWheelPicker } from '../components/TimeSlotWheelPicker';
import { getImageUrl } from '@/utils/imageUrl';
import { formatProfessionalFullName } from '@/utils';
import { cn } from '@/lib/utils';
import {
	buildProfessionalP2PSessionCreateBody,
	buildProfessionalP2PTranscriptionOnlySessionCreateBody,
	buildProfessionalP2PSessionPatchDateBody,
	buildUnrestrictedSlotsForPickerDate,
} from '@/pages/professional/sessions/utils/professionalUnrestrictedSchedule';
import { professionalHasP2PTranscriptionOnlyEligibility } from '@/utils/professionalPermissionTags';
import { ProfessionalConfirmationModalContent } from '@/pages/professional/sessions/components/ProfessionalConfirmationModalContent';
import { ProfessionalSessionBookedModalContent } from '@/pages/professional/sessions/components/ProfessionalSessionBookedModalContent';
import { PROFESSIONAL_BOOKING_STEP_DELAY_MS } from '@/pages/professional/sessions/utils/professionalBookingFlow';
import { type ProfessionalHomeNavigationState } from '@/pages/professional/home/utils/professionalHomeNavigation';
import { waitMs } from '@/utils/waitMs';
import { canProfessionalManageP2PSession } from '@/utils/sessionScheduledBy';
import {
	ProfessionalSessionTypeSelector,
	type ProfessionalSessionCreationMode,
} from '@/components/session/stepScheduleAdmin/ProfessionalSessionTypeSelector';

interface StepScheduleAdminProps {
	/** Fluxo do profissional agendando para si: oculta o card "Horários disponíveis de …". */
	hideProfessionalAvailabilityHeader?: boolean;
	/** PRO: calendário em cima e horário embaixo no container da direita. Admin mantém lado a lado. */
	scheduleLayout?: 'split' | 'stacked';
	/** PRO: botões Voltar / Próxima etapa em largura total abaixo do painel + agenda. */
	footerPosition?: 'inline' | 'full-width';
	/** PRO: data e horário em blocos separados para o layout com wrap. */
	splitScheduleBlocks?: boolean;
	/** PRO: monta blocos + rodapé no layout pai. */
	renderInLayout?: (parts: {
		date: ReactNode;
		time: ReactNode;
		footer: ReactNode;
	}) => ReactNode;
	/** PRO: abre edição do paciente na seção de WhatsApp (revisão). */
	onOpenPatientWhatsAppEdit?: () => void;
}

const StepScheduleAdmin = ({
	hideProfessionalAvailabilityHeader = false,
	scheduleLayout = 'split',
	footerPosition = 'inline',
	splitScheduleBlocks = false,
	renderInLayout,
	onOpenPatientWhatsAppEdit,
}: StepScheduleAdminProps) => {
	const { previousStep, nextStep } = useStep();
	const navigate = useNavigate();
	const {
		openModal,
		setOnConfirm,
		closeModal,
		closeAllModals,
		setIsDisabledConfirm,
	} = useModal();
	const [searchParams] = useSearchParams();
	const [selectedDatePicker, setSelectedDatePicker] = useState({
		date: '',
		time: '',
	});
	const [clinicAddress, setClinicAddress] = useState<Clinics.Address | null>(
		null,
	);
	const [availableDateDataPicker, setAvailableDateDataPicker] =
		useState<Array<string> | null>(null);
	const [availabelSlotsTime, setAvailabelSlotsTime] =
		useState<Array<AvailableDates.SessionTimeSlot> | null>(null);

	const [availableDateId, setAvailableDateId] = useState('');
	const [selectedDate, setSelectedDate] = useState('');
	const [selectedModality, setSelectedModality] =
		useState<Professionals.ServiceModality>('FULL_REMOTE');
	const [proSessionCreationMode, setProSessionCreationMode] =
		useState<ProfessionalSessionCreationMode>('video-call');
	const proCreateTranscriptionOnly =
		proSessionCreationMode === 'transcription-only';

	const [serviceId, setServiceId] = useState('');
	const [defaultTimeSlot, setDefaultTimeSlot] =
		useState<AvailableDates.SessionTimeSlot | null>(null);
	const prefillAppliedRef = useRef(false);

	const encodedParams = searchParams.get('d') || ''; // Garante string vazia se for null
	const decodedParams = decodeUrl<{
		s?: string;
		user_ids?: string; // JSON string com array de IDs
		patient_id?: string;
		professional_id?: string;
		service_id?: string;
		is_group?: boolean | string;
		max_participants?: number;
		prefill_date?: string;
		prefill_hour?: string;
		session_id?: string;
	}>(encodedParams);

	const patientIdFromParams =
		typeof decodedParams?.patient_id === 'string'
			? decodedParams.patient_id
			: '';

	const isProUnrestrictedSchedule = hideProfessionalAvailabilityHeader;

	const sessionIdFromParams =
		typeof decodedParams?.session_id === 'string'
			? decodedParams.session_id
			: '';

	const isProEditMode = isProUnrestrictedSchedule && !!sessionIdFromParams;

	const professionalIdFromParams =
		typeof decodedParams?.professional_id === 'string'
			? decodedParams.professional_id
			: '';

	let selectedUserIdsFromParams: string[] = [];
	if (typeof decodedParams?.user_ids === 'string') {
		try {
			const parsed = JSON.parse(decodedParams.user_ids) as string[];
			if (Array.isArray(parsed)) {
				selectedUserIdsFromParams = parsed.filter(Boolean);
			}
		} catch {
			selectedUserIdsFromParams = [];
		}
	}

	const { data: professionalData } = useGetProfessional(
		professionalIdFromParams,
		{
			enabled: !!professionalIdFromParams,
		},
	);

	const { data: selectedPatientForReview } = useGetPatient(
		patientIdFromParams,
		{
			enabled: isProUnrestrictedSchedule && !!patientIdFromParams,
		},
	);

	const professionalFullName = professionalData
		? formatProfessionalFullName(professionalData)
		: '';

	const { data: availableData, isFetching: isFetchingAvailableData } =
		useGetAvailableDate(professionalData?.id || '', {
			enabled: !!professionalData?.id && !isProUnrestrictedSchedule,
		});

	const schedulePickerLoading = isProUnrestrictedSchedule
		? false
		: isFetchingAvailableData;

	const { data: editingSession } = useGetSession(sessionIdFromParams, {
		enabled: isProEditMode && !!sessionIdFromParams,
	});

	const isProEditSessionLocked =
		isProEditMode &&
		editingSession != null &&
		!canProfessionalManageP2PSession(editingSession);

	const { mutate } = useCreateSession();
	const { mutate: patchSessionDate } = usePatchSessionDate();

	useEffect(() => {
		if (!isProEditSessionLocked) return;

		toast.error('Esta consulta não pode ser editada.');
		navigate('/profissional/home', { replace: true });
	}, [isProEditSessionLocked, navigate]);

	useEffect(() => {
		// Mantemos os dados já existentes na URL e apenas garantimos que `s` reflita este passo.
		// Sempre preservar user_ids (array vazio "[]" para GROUP sem participantes).
		if (encodedParams) {
			const encodeParams = encodeUrl({
				...(decodedParams || {}),
				s: isProUnrestrictedSchedule ? decodedParams?.s ?? '1' : '3',
				user_ids: decodedParams?.user_ids ?? '[]',
			});

			searchParams.set('d', encodeParams);
			return;
		}
	}, [searchParams, encodedParams, decodedParams]);

	useEffect(() => {
		if (isProEditMode && editingSession) {
			setSelectedModality(
				editingSession.is_online ? 'FULL_REMOTE' : 'FULL_PRESENCIAL',
			);
			if (editingSession.service_id) {
				setServiceId(editingSession.service_id);
			}
			return;
		}

		if (professionalData) {
			if (professionalData.service_modality === 'HYBRID') {
				setSelectedModality('FULL_REMOTE');
			} else {
				setSelectedModality(professionalData.service_modality);
			}
		}

		// Definimos o `serviceId` diretamente, sem precisar buscar por nome:
		// 1. Preferimos o `service_id` passado pelo passo anterior.
		// 2. Se não existir, caímos no primeiro serviço vinculado ao profissional.
		const serviceIdFromParams =
			typeof decodedParams?.service_id === 'string'
				? decodedParams.service_id
				: '';

		const serviceIdFromProfessional =
			professionalData?.professional_services?.[0]?.service?.id || '';

		const resolvedServiceId =
			serviceIdFromParams || serviceIdFromProfessional;
		if (resolvedServiceId) {
			setServiceId(resolvedServiceId);
		}
	}, [professionalData, decodedParams, isProEditMode, editingSession]);

	useEffect(() => {
		if (isProUnrestrictedSchedule) {
			setAvailableDateDataPicker([]);
			return;
		}

		const availabilityData =
			availableData?.professionalAvailability?.availability;

		if (availabilityData) {
			// O TimeSlotSelect filtra por horários ainda disponíveis.
			// Então o calendário também precisa só marcar como disponível dias
			// que tenham pelo menos um horário "no futuro".
			const now = new Date();
			const validSlots = availabilityData
				.flatMap((day) => day.hours)
				.filter((slot) => new Date(slot.start_time) > now);

			const localDates = validSlots.map((slot) => {
				const dateObj = new Date(slot.start_time);
				const year = dateObj.getFullYear();
				const month = String(dateObj.getMonth() + 1).padStart(2, '0');
				const day = String(dateObj.getDate()).padStart(2, '0');
				return `${year}-${month}-${day}`;
			});

			setAvailableDateDataPicker(Array.from(new Set(localDates)));
		}
	}, [availableData, isProUnrestrictedSchedule]);

	const isGroupSession =
		decodedParams?.is_group === true || decodedParams?.is_group === 'true';

	const maxParticipantsFromParams =
		typeof decodedParams?.max_participants === 'number'
			? decodedParams.max_participants
			: typeof decodedParams?.max_participants === 'string'
			  ? parseInt(decodedParams.max_participants, 10)
			  : undefined;

	const handleNext = () => {
		if (!serviceId) {
			toast.error(
				'Serviço inválido para este profissional. Selecione novamente.',
			);
			return;
		}

		if (!selectedDate || (!isProUnrestrictedSchedule && !availableDateId)) {
			toast.error('Selecione uma data e horário válidos.');
			return;
		}

		if (!professionalData) {
			toast.error(
				'Ocorreu algum erro inesperado, por favor entre em contato com o suporte',
			);
			return;
		}

		if (hideProfessionalAvailabilityHeader && !patientIdFromParams) {
			toast.error('Selecione um paciente.');
			return;
		}

		if (
			!hideProfessionalAvailabilityHeader &&
			!isGroupSession &&
			selectedUserIdsFromParams.length === 0
		) {
			toast.error(
				'Selecione pelo menos um paciente para sessão individual.',
			);
			return;
		}

		if (
			isGroupSession &&
			selectedUserIdsFromParams.length === 0 &&
			(!maxParticipantsFromParams || maxParticipantsFromParams < 2)
		) {
			toast.error(
				'Para sessão em grupo sem participantes, informe o número máximo de participantes (mín. 2).',
			);
			return;
		}

		// Sempre passar user_ids (obrigatório para GROUP; array vazio [] quando sem participantes)
		const userIds = selectedUserIdsFromParams ?? [];
		const requestBody: Sessions.Create = {
			available_date_id: availableDateId,
			is_online: selectedModality !== 'FULL_PRESENCIAL',
			professional_id: professionalData.id,
			selected_date: selectedDate,
			service_id: serviceId,
			type: isGroupSession ? 'GROUP' : 'DEFAULT',
			user_ids: userIds,
			max_participants: isGroupSession
				? Math.max(
						userIds.length,
						maxParticipantsFromParams ?? userIds.length,
						2,
				  )
				: undefined,
		};

		const reviewTitle = (
			<SessionStepTitle
				icon={<CalendarDays className="w-4" />}
				title="Revisão"
			/>
		);

		if (hideProfessionalAvailabilityHeader) {
			if (!selectedPatientForReview) {
				toast.error('Não foi possível carregar os dados do paciente.');
				return;
			}

			if (isProEditMode && sessionIdFromParams) {
				if (
					editingSession &&
					!canProfessionalManageP2PSession(editingSession)
				) {
					toast.error('Esta consulta não pode ser editada.');
					return;
				}

				const patchBody = buildProfessionalP2PSessionPatchDateBody({
					selectedDate,
				});

				openModal({
					content: (
						<ProfessionalConfirmationModalContent
							patient={selectedPatientForReview}
							scheduleData={selectedDatePicker}
							selectedModality={selectedModality}
							mode="edit"
							onAlterWhatsApp={
								onOpenPatientWhatsAppEdit
									? () => {
											closeModal();
											onOpenPatientWhatsAppEdit();
									  }
									: undefined
							}
						/>
					),
					title: reviewTitle,
				});
				setOnConfirm(() =>
					patchSessionDate(
						{ id: sessionIdFromParams, body: patchBody },
						{
							onSuccess: (session) => {
								closeModal();
								navigate('/profissional/home', {
									state: {
										openSession: session,
									} satisfies ProfessionalHomeNavigationState,
								});
							},
						},
					),
				);
				return;
			}

			const proRequestBody = proCreateTranscriptionOnly
				? buildProfessionalP2PTranscriptionOnlySessionCreateBody({
						patientId: patientIdFromParams,
						selectedDate,
						serviceId,
				  })
				: buildProfessionalP2PSessionCreateBody({
						patientId: patientIdFromParams,
						selectedDate: selectedDate,
						isOnline: selectedModality !== 'FULL_PRESENCIAL',
						serviceId,
				  });

			openModal({
				content: (
					<ProfessionalConfirmationModalContent
						patient={selectedPatientForReview}
						scheduleData={selectedDatePicker}
						selectedModality={selectedModality}
						modalitySummaryOverride={
							proCreateTranscriptionOnly
								? 'Apenas transcrição (sem vídeo na Plumaa)'
								: undefined
						}
						onAlterWhatsApp={
							onOpenPatientWhatsAppEdit
								? () => {
										closeModal();
										onOpenPatientWhatsAppEdit();
								  }
								: undefined
						}
					/>
				),
				title: reviewTitle,
				closeOnConfirm: false,
			});
			setOnConfirm(() => {
				setIsDisabledConfirm(true);
				mutate(proRequestBody, {
					onSuccess: async (session) => {
						await waitMs(PROFESSIONAL_BOOKING_STEP_DELAY_MS);
						closeModal();
						await waitMs(PROFESSIONAL_BOOKING_STEP_DELAY_MS);
						openModal({
							content: (
								<ProfessionalSessionBookedModalContent
									patient={selectedPatientForReview}
									scheduleData={selectedDatePicker}
									selectedModality={selectedModality}
									onViewSession={async () => {
										closeAllModals();
										await waitMs(
											PROFESSIONAL_BOOKING_STEP_DELAY_MS,
										);
										navigate('/profissional/home', {
											state: {
												openSession: session,
											} satisfies ProfessionalHomeNavigationState,
										});
									}}
									onBackToCalendar={async () => {
										closeAllModals();
										await waitMs(
											PROFESSIONAL_BOOKING_STEP_DELAY_MS,
										);
										navigate('/profissional/home');
									}}
								/>
							),
							title: null,
							closeOnConfirm: false,
						});
						setIsDisabledConfirm(false);
					},
					onError: () => {
						setIsDisabledConfirm(false);
					},
				});
			});
			return;
		} else {
			openModal({
				content: (
					<>
						{professionalData && (
							<ConfirmationModalContent
								professionalData={professionalData}
								addressData={clinicAddress}
								scheduleData={selectedDatePicker}
								selectedModality={selectedModality}
								participantIds={selectedUserIdsFromParams}
							/>
						)}
					</>
				),
				title: reviewTitle,
			});
		}

		setOnConfirm(() =>
			mutate(requestBody, { onSuccess: () => nextStep() }),
		);
	};

	const handleSelectedTimeChange = (time: string) => {
		const parsedScheduleObject = JSON.parse(time);
		const formattedTime = formatTimeSlot(
			parsedScheduleObject.start_time,
			parsedScheduleObject.end_time,
		);

		setAvailableDateId(parsedScheduleObject.available_date_id);
		setSelectedDate(parsedScheduleObject.start_time);

		if (formattedTime)
			setSelectedDatePicker((prev) => ({ ...prev, time: formattedTime }));
	};

	const getProDefaultBookingHour = () => new Date().getHours();

	const findProDefaultSlot = (
		validSlots: AvailableDates.SessionTimeSlot[],
		preferredHour = getProDefaultBookingHour(),
	) =>
		validSlots.find(
			(slot) => new Date(slot.start_time).getHours() === preferredHour,
		) ?? null;

	const resolveProPickerPreferredHour = () => {
		if (selectedDate) {
			return new Date(selectedDate).getHours();
		}

		if (defaultTimeSlot?.start_time) {
			return new Date(defaultTimeSlot.start_time).getHours();
		}

		return getProDefaultBookingHour();
	};

	const selectTimeSlotForPicker = (
		slot: AvailableDates.SessionTimeSlot,
		pickerDate: string,
	) => {
		const formattedTime = formatTimeSlot(slot.start_time, slot.end_time);
		setAvailableDateId(slot.available_date_id);
		setSelectedDate(slot.start_time);
		setDefaultTimeSlot(slot);
		setSelectedDatePicker({
			date: pickerDate,
			time: formattedTime ?? '',
		});
	};

	const getValidSlotsForPickerDate = (pickerDateDdMmYyyy: string) => {
		if (isProUnrestrictedSchedule) {
			return buildUnrestrictedSlotsForPickerDate(pickerDateDdMmYyyy);
		}

		const splitDate = pickerDateDdMmYyyy.split('/');
		const formattedDate = `${splitDate[2]}-${splitDate[1]}-${splitDate[0]}`;

		const allHours =
			availableData?.professionalAvailability.availability.flatMap(
				(day) => day.hours,
			) || [];

		const slotsForSelectedDate = allHours.filter((hour) => {
			const localDate = new Date(hour.start_time);
			const localDateString = `${localDate.getFullYear()}-${String(
				localDate.getMonth() + 1,
			).padStart(2, '0')}-${String(localDate.getDate()).padStart(
				2,
				'0',
			)}`;

			return localDateString === formattedDate;
		});

		slotsForSelectedDate.sort(
			(a, b) =>
				new Date(a.start_time).getTime() -
				new Date(b.start_time).getTime(),
		);

		const now = new Date();
		return slotsForSelectedDate.filter(
			(slot) => new Date(slot.start_time) > now,
		);
	};

	const onAvailableDatePicker = (date: string | null) => {
		if (!date) {
			setAvailabelSlotsTime([]);
			setSelectedDatePicker({ date: '', time: '' });
			setDefaultTimeSlot(null);
			return;
		}

		const validSlots = getValidSlotsForPickerDate(date);
		setAvailabelSlotsTime(validSlots);

		if (isProUnrestrictedSchedule) {
			const preferredHour = resolveProPickerPreferredHour();
			const slotToSelect =
				findProDefaultSlot(validSlots, preferredHour) ??
				findProDefaultSlot(validSlots);

			if (slotToSelect) {
				selectTimeSlotForPicker(slotToSelect, date);
				return;
			}
		}

		setSelectedDatePicker({ date, time: '' });
		setDefaultTimeSlot(null);
	};

	useEffect(() => {
		if (prefillAppliedRef.current) return;

		const prefillDateYmd = decodedParams?.prefill_date;
		if (!prefillDateYmd) return;

		const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(prefillDateYmd);
		if (!ymdMatch) {
			prefillAppliedRef.current = true;
			return;
		}

		const pickerDate = `${ymdMatch[3]}/${ymdMatch[2]}/${ymdMatch[1]}`;
		const prefillHourRaw = decodedParams?.prefill_hour;
		const hasHourPrefill =
			prefillHourRaw !== undefined && prefillHourRaw !== '';

		let hourNum: number | null = null;
		if (hasHourPrefill) {
			hourNum = Number.parseInt(String(prefillHourRaw), 10);
			if (Number.isNaN(hourNum) || hourNum < 0 || hourNum > 23) {
				prefillAppliedRef.current = true;
				return;
			}
		}

		const applyDateAndOptionalHour = (
			validSlots: AvailableDates.SessionTimeSlot[],
		) => {
			setAvailabelSlotsTime(validSlots);

			if (hasHourPrefill && hourNum !== null) {
				const matchingSlot = validSlots.find(
					(slot) => new Date(slot.start_time).getHours() === hourNum,
				);

				if (matchingSlot) {
					selectTimeSlotForPicker(matchingSlot, pickerDate);
					return;
				}
			}

			if (isProUnrestrictedSchedule) {
				const defaultSlot = findProDefaultSlot(validSlots);
				if (defaultSlot) {
					selectTimeSlotForPicker(defaultSlot, pickerDate);
					return;
				}
			}

			setDefaultTimeSlot(null);
			setSelectedDatePicker({ date: pickerDate, time: '' });
		};

		if (isProUnrestrictedSchedule) {
			applyDateAndOptionalHour(getValidSlotsForPickerDate(pickerDate));
			prefillAppliedRef.current = true;
			return;
		}

		if (hasHourPrefill) {
			if (
				isFetchingAvailableData ||
				!availableData?.professionalAvailability
			) {
				return;
			}

			if (!availableDateDataPicker?.length) {
				return;
			}

			const availableYmdSet = new Set(availableDateDataPicker);
			if (!availableYmdSet.has(prefillDateYmd)) {
				prefillAppliedRef.current = true;
				return;
			}

			applyDateAndOptionalHour(getValidSlotsForPickerDate(pickerDate));
			prefillAppliedRef.current = true;
			return;
		}

		if (
			isFetchingAvailableData ||
			!availableData?.professionalAvailability
		) {
			return;
		}

		if (!availableDateDataPicker?.length) {
			return;
		}

		const availableYmdSet = new Set(availableDateDataPicker);
		if (!availableYmdSet.has(prefillDateYmd)) {
			prefillAppliedRef.current = true;
			return;
		}

		applyDateAndOptionalHour(getValidSlotsForPickerDate(pickerDate));
		prefillAppliedRef.current = true;
	}, [
		availableData,
		availableDateDataPicker,
		decodedParams?.prefill_date,
		decodedParams?.prefill_hour,
		isFetchingAvailableData,
		isProUnrestrictedSchedule,
	]);

	const handlePreviusStep = () => previousStep();

	useEffect(() => {
		if (selectedModality === 'FULL_PRESENCIAL') {
			setClinicAddress(professionalData?.clinic?.address || null);
		} else {
			setClinicAddress(null);
		}
	}, [selectedModality, professionalData]);

	const proCombinedColumnDateClass = isProUnrestrictedSchedule
		? 'flex w-full shrink-0 flex-col items-stretch gap-4 min-[1200px]:w-[27rem]'
		: 'flex w-full min-w-0 shrink-0 flex-col items-center gap-4 md:w-auto';
	const proCombinedColumnTimeClass = isProUnrestrictedSchedule
		? 'flex w-full shrink-0 flex-col items-stretch justify-start min-[1200px]:w-auto'
		: 'flex w-full min-w-0 shrink-0 flex-col items-center gap-4 md:w-auto';
	const proCombinedCalendarWidthClass = isProUnrestrictedSchedule
		? 'w-full max-w-full shrink-0'
		: 'mx-auto w-full min-w-0 max-w-md sm:max-w-lg md:max-w-2xl';

	const scheduleDatePicker = (
		<AvailableDatePicker
			availableDates={availableDateDataPicker || []}
			onDateChange={(date) => onAvailableDatePicker(date)}
			loading={schedulePickerLoading}
			defaultValue={selectedDatePicker.date || null}
			allowAnyDay={isProUnrestrictedSchedule}
			lockPastMonths={false}
			maxMonthsAhead={isProUnrestrictedSchedule ? 2 : undefined}
			fullWidth={isProUnrestrictedSchedule}
		/>
	);

	const scheduleTimePicker = isProUnrestrictedSchedule ? (
		<TimeSlotWheelPicker
			slots={availabelSlotsTime || []}
			onValueChange={handleSelectedTimeChange}
			disabled={!selectedDatePicker.date}
			defaultValue={defaultTimeSlot}
		/>
	) : (
		<TimeSlotSelect
			slots={availabelSlotsTime || []}
			onValueChange={handleSelectedTimeChange}
			disabled={!selectedDatePicker.date}
			defaultValue={defaultTimeSlot}
		/>
	);

	const datePickerBlock = (
		<div className="flex w-full flex-col items-center space-y-4">
			<SessionStepTitle
				title="Selecione uma data:"
				icon={<CalendarDays className="w-5 shrink-0 text-dark" />}
				className="w-fit"
			/>
			{scheduleDatePicker}
		</div>
	);

	const timePickerInner = (
		<div className="flex w-full flex-col items-center space-y-4">
			<SessionStepTitle
				title="Selecione um horário:"
				icon={<Clock className="w-5 shrink-0 text-dark" />}
				className="w-fit"
			/>
			{scheduleTimePicker}
		</div>
	);

	const timePickerBlock = (
		<div className="flex w-full max-w-full flex-col items-stretch gap-4">
			{timePickerInner}
			{professionalData && !hideProfessionalAvailabilityHeader && (
				<div className="w-full shrink-0">
					<ServiceModalityFeedback
						data={professionalData}
						address={professionalData.clinic?.address}
						onModalityChange={setSelectedModality}
						selectedModality={selectedModality}
					/>
				</div>
			)}
		</div>
	);

	const professionalHeader = !hideProfessionalAvailabilityHeader &&
		professionalData && (
			<div className="flex items-center gap-3 rounded-2xl border border-zinc-100 bg-white px-3 py-2.5 shadow-sm md:w-fit">
				<Avatar
					img_url={getImageUrl(professionalData.profile_photo_url)}
					variant="circle"
					className="size-9"
				/>
				<div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-1">
					<span className="text-xs font-medium uppercase tracking-[0.14em] text-zinc-500">
						Horários disponíveis de
					</span>
					<span className="text-sm font-semibold text-zinc-800 md:text-base">
						{professionalFullName}
					</span>
				</div>
			</div>
		);

	const scheduleCardClassName = isProUnrestrictedSchedule
		? 'flex w-full flex-col rounded-2xl border-2 border-zinc-100 bg-white px-3 py-4 shadow-sm min-[1200px]:h-full min-[1200px]:min-h-0 min-[1200px]:p-4'
		: 'flex w-full flex-col rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm md:p-6';

	const combinedDateTimeScheduleBlock = (
		<div
			className={cn(
				scheduleCardClassName,
				isProUnrestrictedSchedule
					? 'items-stretch justify-start'
					: 'items-center justify-center max-lg:items-stretch max-lg:justify-start',
			)}
		>
			{isProUnrestrictedSchedule &&
			professionalHasP2PTranscriptionOnlyEligibility(professionalData) ? (
				<div className="w-full">
					<h2 className="mb-3 text-sm font-bold text-zinc-800">
						Tipo de sessão
					</h2>
					<ProfessionalSessionTypeSelector
						value={proSessionCreationMode}
						onChange={(mode) => {
							setProSessionCreationMode(mode);
							if (mode === 'transcription-only') {
								setSelectedModality('FULL_PRESENCIAL');
							}
						}}
					/>
				</div>
			) : null}
			<div
				className={cn(
					'w-full',
					isProUnrestrictedSchedule &&
						professionalHasP2PTranscriptionOnlyEligibility(
							professionalData,
						) &&
						'mt-10',
				)}
			>
				{isProUnrestrictedSchedule ? (
					<h2 className="text-sm font-bold text-zinc-800">
						Selecione uma data e um horário
					</h2>
				) : null}
				<div
					className={cn(
						'flex w-full max-w-full flex-col items-center gap-4',
						isProUnrestrictedSchedule && 'mt-4',
						isProUnrestrictedSchedule
							? 'min-[1200px]:flex-row min-[1200px]:flex-nowrap min-[1200px]:items-start min-[1200px]:justify-start min-[1200px]:gap-8'
							: 'md:flex-row md:flex-wrap md:items-start md:justify-center',
					)}
				>
					<div className={proCombinedColumnDateClass}>
						{!isProUnrestrictedSchedule ? (
							<SessionStepTitle
								title="Selecione uma data:"
								icon={
									<CalendarDays className="w-5 shrink-0 text-dark" />
								}
								className="justify-center"
							/>
						) : null}
						<div className={proCombinedCalendarWidthClass}>
							{scheduleDatePicker}
						</div>
					</div>
					<div className={proCombinedColumnTimeClass}>
						{!isProUnrestrictedSchedule ? (
							<SessionStepTitle
								title="Selecione um horário"
								icon={
									<Clock className="w-5 shrink-0 text-dark" />
								}
								className="justify-center"
							/>
						) : null}
						{scheduleTimePicker}
					</div>
				</div>
			</div>
		</div>
	);

	const scheduleContent = (
		<>
			{professionalHeader}
			{scheduleLayout === 'stacked' && !splitScheduleBlocks ? (
				<div className="flex w-full flex-col gap-6 rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm md:p-6">
					{datePickerBlock}
					<div className="border-t border-zinc-100 pt-6">
						{timePickerBlock}
					</div>
				</div>
			) : scheduleLayout === 'stacked' && splitScheduleBlocks ? null : (
				<section className="flex w-full flex-col justify-center gap-8 md:flex-row md:gap-0">
					{datePickerBlock}
					<div className="mx-4 hidden border border-s md:block" />
					{timePickerBlock}
				</section>
			)}
		</>
	);

	const stepFooter = (
		<div
			className={cn(
				'w-full shrink-0 border-t border-zinc-100',
				splitScheduleBlocks ? 'pt-4' : 'pt-5 sm:pt-6',
				footerPosition === 'inline' && 'mt-6 sm:mt-8',
				footerPosition === 'full-width' && 'col-span-full',
			)}
		>
			<StepButtonContainer
				onHandleNext={handleNext}
				onHandlePrevius={
					hideProfessionalAvailabilityHeader
						? undefined
						: handlePreviusStep
				}
				disabledNextButton={
					!selectedDatePicker.date ||
					!selectedDatePicker.time ||
					(hideProfessionalAvailabilityHeader &&
						!patientIdFromParams) ||
					isProEditSessionLocked
				}
			/>
		</div>
	);

	if (footerPosition === 'full-width') {
		if (renderInLayout && splitScheduleBlocks) {
			return renderInLayout({
				date: combinedDateTimeScheduleBlock,
				time: null,
				footer: stepFooter,
			});
		}

		const scheduleBlock = (
			<div className="flex w-full flex-col space-y-6">
				{scheduleContent}
			</div>
		);

		if (renderInLayout) {
			return renderInLayout({
				date: scheduleBlock,
				time: null,
				footer: stepFooter,
			});
		}

		return (
			<>
				{scheduleBlock}
				{stepFooter}
			</>
		);
	}

	return (
		<section className="flex w-full flex-col justify-between space-y-8">
			{scheduleContent}
			{stepFooter}
		</section>
	);
};

export { StepScheduleAdmin };
